import React, { useState, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Activity, Sparkles, Library, Settings, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NavItem {
  path: string
  label: string
  icon: React.ReactNode
  activeColor: string
}

const NAV_ITEMS: NavItem[] = [
  {
    path: '/',
    label: 'Pulse',
    icon: <Activity className="w-5 h-5" />,
    activeColor: '#10B981'
  },
  {
    path: '/constellation',
    label: 'Grafo',
    icon: <Sparkles className="w-5 h-5" />,
    activeColor: '#8B5CF6'
  },
  {
    path: '/library',
    label: 'Library',
    icon: <Library className="w-5 h-5" />,
    activeColor: '#3B82F6'
  },
  {
    path: '/settings',
    label: 'Config',
    icon: <Settings className="w-5 h-5" />,
    activeColor: '#F59E0B'
  }
]

// Minimum touch target size for accessibility (44x44px)
const TOUCH_TARGET_SIZE = 44

// Swipe gesture thresholds
const SWIPE_THRESHOLD = 50 // Minimum distance for swipe
const SWIPE_VELOCITY_THRESHOLD = 0.5 // Minimum velocity

interface TouchState {
  startX: number
  startY: number
  startTime: number
  currentX: number
  currentY: number
}

export function MobileNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const containerRef = useRef<HTMLElement>(null)
  const [touchState, setTouchState] = useState<TouchState | null>(null)
  const [isPressed, setIsPressed] = useState<string | null>(null)

  // Get current nav index
  const currentIndex = NAV_ITEMS.findIndex(item =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  )

  // Handle touch start
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    setTouchState({
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      currentX: touch.clientX,
      currentY: touch.clientY
    })
  }, [])

  // Handle touch move
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchState) return

    const touch = e.touches[0]
    setTouchState(prev => prev ? {
      ...prev,
      currentX: touch.clientX,
      currentY: touch.clientY
    } : null)
  }, [touchState])

  // Handle touch end - detect swipe gestures
  const handleTouchEnd = useCallback(() => {
    if (!touchState) return

    const deltaX = touchState.currentX - touchState.startX
    const deltaY = touchState.currentY - touchState.startY
    const deltaTime = Date.now() - touchState.startTime
    const velocity = Math.abs(deltaX) / deltaTime

    // Check if horizontal swipe (not vertical scroll)
    if (Math.abs(deltaX) > Math.abs(deltaY) &&
        Math.abs(deltaX) > SWIPE_THRESHOLD &&
        velocity > SWIPE_VELOCITY_THRESHOLD) {

      const direction = deltaX > 0 ? -1 : 1 // Swipe left = next, swipe right = prev
      const newIndex = currentIndex + direction

      if (newIndex >= 0 && newIndex < NAV_ITEMS.length) {
        // HAPTIC FEEDBACK HINT: Call navigator.vibrate(10) here if available
        // if ('vibrate' in navigator) navigator.vibrate(10)
        navigate(NAV_ITEMS[newIndex].path)
      }
    }

    setTouchState(null)
  }, [touchState, currentIndex, navigate])

  // Handle button press feedback
  const handlePressStart = useCallback((path: string) => {
    setIsPressed(path)
    // HAPTIC FEEDBACK HINT: Light haptic on press start
    // if ('vibrate' in navigator) navigator.vibrate(5)
  }, [])

  const handlePressEnd = useCallback(() => {
    setIsPressed(null)
  }, [])

  // Handle navigation with haptic hint
  const handleNavigate = useCallback((path: string) => {
    // HAPTIC FEEDBACK HINT: Medium haptic on navigation
    // if ('vibrate' in navigator) navigator.vibrate(15)
    navigate(path)
  }, [navigate])

  return (
    <nav
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      aria-label="Main navigation"
    >
      {/* Glass background with enhanced blur */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl border-t border-white/10" />

      {/* Swipe indicator - shows during horizontal swipe */}
      {touchState && Math.abs(touchState.currentX - touchState.startX) > 20 && (
        <div
          className={cn(
            "absolute top-0 h-0.5 bg-rsb-accent transition-all duration-75",
            touchState.currentX - touchState.startX > 0 ? "left-0" : "right-0"
          )}
          style={{
            width: `${Math.min(Math.abs(touchState.currentX - touchState.startX) / 3, 100)}px`,
            opacity: Math.min(Math.abs(touchState.currentX - touchState.startX) / SWIPE_THRESHOLD, 1)
          }}
        />
      )}

      {/* Safe area padding for notched devices */}
      <div className="relative flex items-center justify-around px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path))
          const isPressing = isPressed === item.path

          return (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              onTouchStart={() => handlePressStart(item.path)}
              onTouchEnd={handlePressEnd}
              onTouchCancel={handlePressEnd}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.label}
              style={{
                // Ensure minimum touch target size
                minWidth: TOUCH_TARGET_SIZE,
                minHeight: TOUCH_TARGET_SIZE
              }}
              className={cn(
                "relative flex flex-col items-center justify-center",
                "w-[72px] h-16 rounded-2xl",
                "transition-all duration-200 ease-out",
                // Touch feedback
                "active:scale-95",
                // Focus states for accessibility
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rsb-accent focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                // Color states
                isActive
                  ? "text-white"
                  : "text-white/40 hover:text-white/60",
                // Press state
                isPressing && "scale-95"
              )}
            >
              {/* Active indicator glow - animated */}
              {isActive && (
                <div
                  className={cn(
                    "absolute inset-0 rounded-2xl opacity-25",
                    "transition-all duration-300",
                    "animate-pulse"
                  )}
                  style={{
                    backgroundColor: item.activeColor,
                    boxShadow: `0 0 30px ${item.activeColor}60`
                  }}
                />
              )}

              {/* Ripple effect on press */}
              {isPressing && (
                <div
                  className="absolute inset-0 rounded-2xl bg-white/10 animate-ping"
                  style={{ animationDuration: '0.4s', animationIterationCount: 1 }}
                />
              )}

              {/* Icon with scale animation */}
              <div
                className={cn(
                  "relative z-10 transition-all duration-200",
                  isActive && "drop-shadow-lg"
                )}
                style={{
                  color: isActive ? item.activeColor : undefined,
                  transform: isActive ? 'scale(1.15)' : 'scale(1)'
                }}
              >
                {item.icon}
              </div>

              {/* Label with fade transition */}
              <span
                className={cn(
                  "relative z-10 text-[10px] font-medium mt-1.5",
                  "transition-all duration-200"
                )}
                style={{
                  color: isActive ? item.activeColor : undefined,
                  opacity: isActive ? 1 : 0.7
                }}
              >
                {item.label}
              </span>

              {/* Active indicator dot */}
              {isActive && (
                <div
                  className={cn(
                    "absolute -top-0.5 w-1.5 h-1.5 rounded-full",
                    "transition-all duration-300",
                    "shadow-lg"
                  )}
                  style={{
                    backgroundColor: item.activeColor,
                    boxShadow: `0 0 8px ${item.activeColor}`
                  }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Bottom safe area gradient for better visual separation */}
      <div className="absolute bottom-0 left-0 right-0 h-[env(safe-area-inset-bottom)] bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
    </nav>
  )
}

// Floating Action Button for mobile
interface MobileFabProps {
  icon?: React.ReactNode
  onClick: () => void
  label: string
  className?: string
}

export function MobileFab({
  icon = <Plus className="w-6 h-6" />,
  onClick,
  label,
  className
}: MobileFabProps) {
  const [isPressed, setIsPressed] = useState(false)

  return (
    <button
      onClick={() => {
        // HAPTIC FEEDBACK HINT: Strong haptic on FAB press
        // if ('vibrate' in navigator) navigator.vibrate([10, 50, 10])
        onClick()
      }}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      aria-label={label}
      className={cn(
        "fixed z-50 md:hidden",
        "bottom-24 right-4", // Above mobile nav
        "w-14 h-14 rounded-full",
        "bg-rsb-accent text-white",
        "shadow-lg shadow-rsb-accent/30",
        "transition-all duration-200",
        "hover:shadow-rsb-accent/50 hover:scale-105",
        "active:scale-95",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-rsb-bg",
        isPressed && "scale-90 shadow-rsb-accent/20",
        className
      )}
      style={{
        // Ensure minimum touch target
        minWidth: TOUCH_TARGET_SIZE,
        minHeight: TOUCH_TARGET_SIZE
      }}
    >
      <span className={cn(
        "flex items-center justify-center transition-transform duration-200",
        isPressed && "scale-90"
      )}>
        {icon}
      </span>
    </button>
  )
}
